import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { PageEnum, TABLEPAI } from "@/enums/pageEnum";
import { useTranslation } from "react-i18next";
import { BasicTable, PropsRef, FilterComponet, Status } from "@/components";
import clsx from "clsx";
import { useCallback, useMemo, useRef, useEffect, useState } from "react";
import { Button, Collapse, Skeleton } from "antd";
import { FilterParams } from "@/facilities/model/user";
import { getCaseCount } from "@api";
import { isEqual } from "lodash-es";
import { GetExpandIcon, LiftTcLongSuffix } from "@/utils/commonConfig";
import { getTimeToString } from "@/utils/toolt";
import {
  priorityOptions,
  mediaOptions,
  subcategoryOptions,
  statusOptions,
  feedbackOptions,
  residOptions,
  categoryOptions,
} from "./constant";

interface CountInfo {
  newStatusCnt?: number;
  openStatusCnt?: number;
  notCompleteStatusCnt?: number;
  completeStatusCnt?: number;
  closedStatusCnt?: number;
  dispatchedStatusCnt?: number;
  collectedStatusCnt?: number;
  cancelledStatusCnt?: number;
}

export default function CaseManagement() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigation = useNavigate();
  const tableRef = useRef<PropsRef>(null);
  const [countInfo, setCountInfo] = useState<CountInfo>({});

  const colDiv = useCallback(
    (name: React.ReactNode, number?: number, isAddBord = true) => {
      return (
        <div
          className={clsx(
            "text-center flex-1 flex justify-between flex-col",
            isAddBord
              ? "relative before:border-l before:border-[#BDBDBD] before:absolute before:top-1/2 before:right-0 before:-translate-y-1/2 before:h-10 before:w-[1px]"
              : ""
          )}
        >
          <p className="text-[#00000099] dark:text-[#ffffffd9] text-[16px] leading-5 h-11">
            {name}
          </p>
          <p className="text-[28px] font-bold text-black dark:text-[#ffffffd9]">
            {number || "0"}
          </p>
        </div>
      );
    },
    []
  );

  const filtersFileds: FilterParams[] = useMemo(() => {
    const fieldProps = { style: { width: "260px" } };
    return [
      {
        ...LiftTcLongSuffix(t),
        fieldProps: { divClass: "w-[390px] !mr-4" },
        labelCol: { span: 7 },
        wrapperCol: { span: 16, offset: 1 },
      },
      {
        field: "status",
        label: t("caseStatus"),
        component: "select",
        options: statusOptions,
        fieldProps,
      },
      {
        field: "category",
        label: t("category"),
        component: "select",
        options: categoryOptions,
        fieldProps,
      },
      {
        field: "subcategory",
        label: t("Subcategory"),
        component: "select",
        options: subcategoryOptions,
        fieldProps,
      },
      {
        field: "priority",
        label: t("priorityT"),
        component: "select",
        options: priorityOptions,
        fieldProps,
      },
      {
        field: "feedbackType",
        label: t("feedbackType"),
        component: "select",
        options: feedbackOptions,
        fieldProps,
      },
      {
        field: "liftTechnicianName",
        label: t("caseFollowed"),
        component: "text",
        fieldProps,
      },
      {
        field: "channel",
        label: t("channel"),
        component: "select",
        options: residOptions,
        fieldProps,
      },
      {
        field: "media",
        label: t("media"),
        component: "select",
        fieldProps,
        options: mediaOptions,
      },
    ];
  }, [t]);

  const isIndex = useMemo(() => {
    return pathname === PageEnum.CASE_MANAGDE;
  }, [pathname]);

  useEffect(() => {
    if (!isIndex) return;
    getCaseCount().then((res: { data: { body: CountInfo } }) => {
      setCountInfo(res.data.body);
    });
  }, [isIndex]);

  const getCount = useMemo(() => {
    if (isEqual(countInfo, {})) return <Skeleton active />;
    return (
      <>
        {colDiv(t("new"), countInfo.newStatusCnt)}
        {colDiv(t("open"), countInfo.openStatusCnt)}
        {colDiv(
          <>
            {t("DEPLOYMENT")}
            <br />({t("notComp")})
          </>,
          countInfo.notCompleteStatusCnt
        )}
        {colDiv(
          <>
            {t("DEPLOYMENT")}
            <br />({t("comp")})
          </>,
          countInfo.completeStatusCnt
        )}
        {colDiv(t("case_dispatched"), countInfo.dispatchedStatusCnt)}
        {colDiv(t("case_collected"), countInfo.collectedStatusCnt)}
        {colDiv(t("finCold"), countInfo.closedStatusCnt, false)}
        <div className="text-right flex-1">
          <Button
            type="primary"
            onClick={() => navigation(PageEnum.CASE_MANAGDEADD)}
          >
            {t("newCase")}
          </Button>
        </div>
      </>
    );
  }, [countInfo, t, colDiv, navigation]);

  if (!isIndex) return <Outlet />;

  return (
    <div className="p-4">
      <div className="bg-[#fff] dark:bg-[#141414] p-4 mb-4 flex justify-between items-center">
        {getCount}
      </div>
      <div className="mt-4 py-3 reportFilters bg-[#fff] mb-0.5 dark:bg-[#141414]">
        <Collapse
          defaultActiveKey={["1"]}
          expandIconPosition="end"
          expandIcon={({ isActive }) => GetExpandIcon(isActive)}
          items={[
            {
              key: "1",
              label: (
                <h3 className="text-[20px] font-medium">{t("filters")}</h3>
              ),
              children: (
                <FilterComponet
                  className="mb-[2px] filtersComplant caseFilter !py-0 !pl-0"
                  submitNode={<>{t("search")}</>}
                  fieldsProp={filtersFileds}
                  submit={() => {
                    tableRef.current?.afreshTableData();
                  }}
                  reset={() => {
                    tableRef.current?.afreshTableData({ page: 1 }, true);
                  }}
                >
                  <p className="h-[1px] bg-[#E8E9ED] dark:bg-[#ffffff21] w-full"></p>
                </FilterComponet>
              ),
            },
          ]}
        />
      </div>
      <BasicTable
        columns={[
          { title: t("TC"), dataIndex: "liftTc" },
          { title: t("caseID"), dataIndex: "caseNo" },
          { title: t("category"), dataIndex: "categoryFormat" },
          {
            title: t("loggedDate"),
            dataIndex: "createdAt",
            renderText: (text: number) => getTimeToString(text),
          },
          { title: t("postCo"), dataIndex: "buildingPostcode" },
          {
            title: t("updatedAt"),
            dataIndex: "updatedAt",
            renderText: (text: number) => getTimeToString(text),
          },
          {
            title: t("priorityT"),
            dataIndex: "priority",
            renderText: (text: string) => <Status status={text} />,
          },
          {
            title: t("feedbackChannel"),
            dataIndex: "media",
            renderText: (text: string) => t(text),
          },
          {
            title: t("status"),
            dataIndex: "status",
            renderText: (text: string) => <Status status={text} />,
          },
        ]}
        onCilickRow={(record: { id: string }) => {
          navigation(`${PageEnum.CASE_MANAGDETAL}/${record?.id}`);
        }}
        tableName="lmd"
        className="min-h-max caseTable"
        isShowCheckbox={false}
        isDefaultToolBar={false}
        ref={tableRef}
        headerTitle={() => t("caseList")}
        requestUrl={TABLEPAI.LIFT_COMPLAINT}
      />
    </div>
  );
}
